import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { dequal } from "dequal";
import type { UserSelectionState } from "@/store/slices/userSelection/userSelection";
import type { AppDispatch } from "@/store/store";

// ─── State ───────────────────────────────────────────────────────────────────

/**
 * Keys from `UserSelectionState` whose change requires a new API call.
 *
 * When `setPendingSelection` is dispatched with ANY of these keys present,
 * `PendingSelectionMeta.loading` is set to `true`.  It remains `true` until
 * the selection listener commits the pending change and the next dispatch
 * (or a format-only dispatch) resolves it back to `false`.
 *
 * Keys intentionally excluded (cosmetic / local-only, no API impact):
 *   hiddenBrands, hiddenMarkets, hiddenSegments,
 *   hiddenQuestions, trendsMetric, trendsResponse, groupedBy, viewId,
 *   reportId, module
 */
export const API_TRIGGERING_KEYS = new Set<keyof UserSelectionState>([
  "brands",
  "markets",
  "segments",
  "filters",
  "base",
  "statTest",
  "periodType",
  "periodStartDate",
  "periodEndDate",
  "period",
  "postPeriodType",
  "postStartDate",
  "postEndDate",
  "mode",
  "screenType",
  "secondaryBrands",
  "scoreType",
  "ageRange",
  "profileMetric",
  "profileGroup",
  "profileResponse",
  "questionOptions",
  "visualizationType",
  "brandSetVersion",
]);

/**
 * Meta-flags that track whether the initial filters and segments have been
 * fetched for the active group.  The `selectionListener` waits for both to
 * be `true` before running its prune cycle so it never evaluates against an
 * empty segments / filters store.
 *
 * These flags survive `clearPendingSelection` (they are only reset explicitly
 * when a market-change dispatch includes `filtersReady: false, segmentsReady: false`
 * in the same `setPendingSelection` payload).
 */
export interface PendingSelectionMeta {
  filtersReady: boolean;
  segmentsReady: boolean;
  /**
   * Whether the initial questions fetch for the active market has completed.
   *
   * Defaults to `false`. Set to `true` by `markQuestionsReady()` on EQ
   * mount — the catalog ships in the bootstrap payload.
   *
   * The `selectionListener` checks this flag (in addition to `filtersReady`
   * and `segmentsReady`) when the current module is `exclusivequestions`,
   * preventing `pruneInvalidFields` from running against an empty question
   * store and incorrectly wiping valid `questionOptions` from the URL.
   *
   * For all other modules this flag is not checked, so it never blocks the
   * prune cycle on non-EQ pages.
   *
   * Survives `clearPendingSelection()` — same pattern as `filtersReady`.
   */
  questionsReady: boolean;
  /**
   * Segment IDs that were present in the initial page-load URL but are NOT
   * found in the user's loaded segment library for the active market.
   *
   * Captured by `FetchFiltersAndSegments` after `fetchSegmentsByMarketId`
   * resolves (so the store is populated) but before `markSegmentsReady()`
   * unblocks the `selectionListener` (so the URL hasn't been pruned yet).
   *
   * `ValidateSegment` reads this field and calls the validate API.
   * Cleared by `clearPendingValidationSegments()` once consumed.
   *
   * Survives `clearPendingSelection()` — same pattern as `filtersReady`.
   */
  pendingValidationSegments: number[];
  /**
   * Whether a user-selection change that impacts the API is currently
   * in-flight.
   *
   * Lifecycle:
   *   1. `setPendingSelection` is dispatched with at least one key from
   *      `API_TRIGGERING_KEYS` → `loading` is set to `true`.
   *   2. The selection listener commits the pending change to `userSelection`.
   *      `loading` stays `true` (preserved by `clearPendingSelection`) until
   *      `useModeViewData` completes its fetch + format cycle, after which
   *      `hasPendingAPIChange` resolves to `false` via the Redux selector.
   *
   * Survives `clearPendingSelection()` — same pattern as `filtersReady`.
   */
  loading: boolean;
}

/**
 * Ephemeral staging area for in-flight user-selection changes.
 *
 * This slice holds only the fields that the user has mutated but that have
 * NOT yet been validated, pruned, and committed to `userSelection`, plus the
 * two readiness meta-flags above.
 *
 * Lifecycle:
 *   1. UI dispatches `setPendingSelection` with the changed field(s).
 *   2. RTK Listener middleware (`selectionListener`) reads this slice,
 *      merges it with the current `userSelection`, resolves a new
 *      `screenConfig`, prunes invalid fields, then commits the clean state
 *      via `syncFromURL` and clears this slice via `clearPendingSelection`.
 *
 * IMPORTANT: This slice is **never read directly by UI components**.
 * It exists solely as a signalling mechanism for the listener middleware.
 */
export type PendingSelectionState = Partial<UserSelectionState> &
  PendingSelectionMeta;

const initialState: PendingSelectionState = {
  filtersReady: false,
  segmentsReady: false,
  questionsReady: false,
  pendingValidationSegments: [],
  loading: false,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

export const pendingSelectionSlice = createSlice({
  name: "pendingSelection",
  initialState,
  reducers: {
    /**
     * Stage one or more selection field changes.
     *
     * **Merges** the new payload onto the existing pending state so that
     * fields staged by a previous (not-yet-processed) dispatch are preserved
     * when a subsequent dispatch only touches different fields.
     *
     * Example race condition this prevents:
     *   1. `setPendingSelection({ markets:[42], filtersReady:false, segmentsReady:false })`
     *      → Listener A starts but suspends at `await condition(isDataReady)`.
     *   2. `setPendingSelection({ brands:[1,2] })` arrives while Listener A is
     *      still suspended.
     *      → With the previous REPLACE behaviour, markets would have been wiped
     *        from the pending slice and Listener B would have only seen brands.
     *      → With MERGE, the pending slice becomes `{ markets:[42], brands:[1,2] }`
     *        so Listener B (which cancels Listener A) processes both correctly.
     *
     * Conflict resolution (same key in both old pending and new payload):
     *   The new payload always wins, so a second `setPendingSelection({ markets:[99] })`
     *   correctly overwrites the previously staged `markets:[42]`.
     *
     * The `filtersReady` / `segmentsReady` meta-flags are handled separately
     * (not via the merge) so they can be explicitly reset in one atomic dispatch:
     *
     * ```ts
     * dispatch(setPendingSelection({ markets: [42], filtersReady: false, segmentsReady: false }))
     * ```
     *
     * @example
     * dispatch(setPendingSelection({ markets: [42], filtersReady: false, segmentsReady: false }))
     * dispatch(setPendingSelection({ brands: [1, 2], mode: "TREND" }))
     */
    setPendingSelection: (
      state,
      action: PayloadAction<Partial<UserSelectionState> & Partial<PendingSelectionMeta>>,
    ): PendingSelectionState => {
      // Destructure the optional meta-flags from the payload so they are never
      // forwarded into the UserSelection portion of the state.
      const {
        filtersReady: payloadFiltersReady,
        segmentsReady: payloadSegmentsReady,
        questionsReady: payloadQuestionsReady,
        ...selectionPayload
      } = action.payload;

      // Destructure meta-flags out of the current state so that spreading
      // `stateSelectionPart` never accidentally duplicates them (they are
      // set explicitly at the end of this return value).
      const {
        filtersReady: stateFR,
        segmentsReady: stateSR,
        questionsReady: stateQR,
        loading: stateLoading,
        ...stateSelectionPart
      } = state;

      // If any of the changed keys will require a new API call, mark loading.
      const triggersApiCall = Object.keys(selectionPayload).some((k) =>
        API_TRIGGERING_KEYS.has(k as keyof UserSelectionState),
      );

      return {
        // ── MERGE: preserve any un-processed selection fields from a previous
        // dispatch whose listener was cancelled before it could call
        // `clearPendingSelection`.  Without this merge, markets staged in step 1
        // above would be silently dropped when the brands-only step 2 arrived.
        ...stateSelectionPart,
        // New payload fields override stale pending fields key-by-key.
        ...selectionPayload,
        // When the caller explicitly passes a flag value (e.g. `false` for a
        // market-change reset), honour it.  Otherwise preserve the current flag.
        filtersReady:
          payloadFiltersReady !== undefined ? payloadFiltersReady : stateFR,
        segmentsReady:
          payloadSegmentsReady !== undefined ? payloadSegmentsReady : stateSR,
        questionsReady:
          payloadQuestionsReady !== undefined ? payloadQuestionsReady : stateQR,
        // Only flip loading → true when this dispatch carries an API-triggering
        // key.  For format-only dispatches (trendsMetric, visualizationType, …)
        // preserve the current flag so a mid-flight loader is never cancelled.
        loading: triggersApiCall ? true : stateLoading,
      };
    },

    /**
     * Reset the pending selection back to an empty object while preserving
     * the readiness meta-flags and any pending segment validation data.
     *
     * Called by the RTK Listener after it has successfully merged, validated,
     * and committed the pending changes to `userSelection`.
     */
    clearPendingSelection: (state): PendingSelectionState => {
      return {
        filtersReady: state.filtersReady,
        segmentsReady: state.segmentsReady,
        questionsReady: state.questionsReady,
        pendingValidationSegments: state.pendingValidationSegments,
        // `loading` intentionally preserved — the flag must remain `true`
        // until `useModeViewData` completes its fetch + format cycle, even
        // though the ephemeral selection fields are cleared here.
        loading: state.loading,
      };
    },

    /**
     * Signal that the initial filters fetch for the active group has
     * completed.  Dispatched by `FetchFiltersAndSegments` after
     * `fetchFiltersByMarketId` resolves.
     */
    markFiltersReady: (state) => {
      state.filtersReady = true;
    },

    /**
     * Signal that the initial segments fetch for the active group has
     * completed.  Dispatched by `FetchFiltersAndSegments` after
     * `fetchSegmentsByMarketId` resolves.
     */
    markSegmentsReady: (state) => {
      state.segmentsReady = true;
    },

    /**
     * Signal that the initial questions fetch for the active market has
     * completed on the Exclusive Questions page.
     *
     * Dispatched by `ExclusiveQuestions.tsx` on mount — the catalog ships in
     * the bootstrap payload. Unblocks the `selectionListener` prune cycle for the EQ module so that
     * `questionOptions` are validated against a populated question store.
     */
    markQuestionsReady: (state) => {
      state.questionsReady = true;
    },

    /**
     * Reset both readiness flags to `false`.
     *
     * Kept for explicit / programmatic resets.  In normal UI flows the flags
     * are reset atomically by passing `filtersReady: false, segmentsReady: false`
     * directly in the `setPendingSelection` payload (see `MarketsDropdownView`).
     */
    resetReadinessFlags: (state) => {
      state.filtersReady = false;
      state.segmentsReady = false;
    },

    /**
     * Store segment IDs that were in the initial URL but are not present in
     * the user's loaded segment library.  Dispatched by `FetchFiltersAndSegments`
     * during the initial market fetch, before `markSegmentsReady()` unblocks the
     * `selectionListener`.
     */
    setPendingValidationSegments: (
      state,
      action: PayloadAction<number[]>,
    ) => {
      state.pendingValidationSegments = action.payload;
    },

    /**
     * Clear the pending validation segments once `ValidateSegment` has
     * consumed them (called the validate API or determined no action needed).
     */
    clearPendingValidationSegments: (state) => {
      state.pendingValidationSegments = [];
    },

    /**
     * Mark the API change cycle as complete.
     *
     * Called by `useModeViewData` on every exit path of the fetch + format
     * cycle (success, API error, network error).  Sets `loading` to `false`
     * so that `hasPendingAPIChange` (read by `useModeViewData` via the Redux
     * selector) resolves to `false` and the loader is hidden.
     *
     * Only has an effect when `loading` is `true` — safe to call even if it
     * was never set (e.g. format-only dispatches).
     */
    clearPendingAPIChange: (state) => {
      state.loading = false;
    },
  },
});

export const {
  setPendingSelection,
  clearPendingSelection,
  markFiltersReady,
  markSegmentsReady,
  markQuestionsReady,
  resetReadinessFlags,
  setPendingValidationSegments,
  clearPendingValidationSegments,
  clearPendingAPIChange,
} = pendingSelectionSlice.actions;

/**
 * Thunk version of `setPendingSelection` that skips any selection key whose
 * incoming value is already identical to the current value in `userSelection`.
 *
 * This prevents redundant listener cycles and spurious `loading: true` flips
 * when a UI component re-dispatches a value that has not actually changed.
 *
 * Meta-flags (`filtersReady`, `segmentsReady`, `questionsReady`) are **always**
 * forwarded as-is — they are never filtered out, because they are control
 * signals rather than data values.
 *
 * If every selection key in the payload already matches `userSelection`, and no
 * meta-flag was explicitly supplied, the dispatch is a no-op.
 *
 * @example
 * // Only dispatches if `brands` in userSelection differs from [1, 2].
 * dispatch(setPendingSelectionIfChanged({ brands: [1, 2] }));
 */
export const setPendingSelectionIfChanged =
  (payload: Partial<UserSelectionState> & Partial<PendingSelectionMeta>) =>
  (
    dispatch: AppDispatch,
    getState: () => { userSelection: UserSelectionState },
  ): void => {
    const currentUserSelection = getState().userSelection;

    const {
      filtersReady,
      segmentsReady,
      questionsReady,
      loading,
      pendingValidationSegments,
      ...selectionPayload
    } = payload as Partial<UserSelectionState> & Partial<PendingSelectionMeta>;

    // Keep only the keys whose value differs from what is already committed in
    // userSelection.  Uses `dequal` for deep equality so arrays/objects are
    // compared by value, not by reference.
    const changedSelectionPayload = Object.fromEntries(
      Object.entries(selectionPayload).filter(
        ([key, value]) =>
          !dequal(
            value,
            currentUserSelection[key as keyof UserSelectionState],
          ),
      ),
    ) as Partial<UserSelectionState>;

    // Collect only the meta-flags that were explicitly supplied by the caller.
    const metaFlags: Partial<PendingSelectionMeta> = {};
    if (filtersReady !== undefined) metaFlags.filtersReady = filtersReady;
    if (segmentsReady !== undefined) metaFlags.segmentsReady = segmentsReady;
    if (questionsReady !== undefined) metaFlags.questionsReady = questionsReady;
    if (loading !== undefined) metaFlags.loading = loading;
    if (pendingValidationSegments !== undefined)
      metaFlags.pendingValidationSegments = pendingValidationSegments;

    // If nothing changed and no meta-flag was explicitly supplied, skip the
    // dispatch entirely to avoid a needless listener run.
    if (
      Object.keys(changedSelectionPayload).length === 0 &&
      Object.keys(metaFlags).length === 0
    ) {
      return;
    }

    dispatch(
      setPendingSelection({
        ...changedSelectionPayload,
        ...metaFlags,
      }),
    );
  };

export default pendingSelectionSlice.reducer;
